import { ref, computed, toValue } from "vue";

export function useSelection() {
  const selectedTargetTokens = ref(new Set());
  const selectedModalityIds = ref(new Set());

  const selectedCount = computed(() => selectedModalityIds.value.size + selectedTargetTokens.value.size);
  const hasSelection = computed(() => selectedCount.value > 0);

  function isTargetSelected(token) {
    return selectedTargetTokens.value.has(token);
  }

  function isModalitySelected(quotaId) {
    return selectedModalityIds.value.has(quotaId);
  }

  function isQuestionSelected(quotaIds) {
    if (quotaIds.length === 0) return false;
    return quotaIds.every((id) => selectedModalityIds.value.has(id));
  }

  function toggleTarget(target, modalityIds) {
    const next = new Set(selectedTargetTokens.value);
    const nextMods = new Set(selectedModalityIds.value);
    if (next.has(target.token)) {
      next.delete(target.token);
      modalityIds.forEach((id) => nextMods.delete(id));
    } else {
      next.add(target.token);
      modalityIds.forEach((id) => nextMods.add(id));
    }
    selectedTargetTokens.value = next;
    selectedModalityIds.value = nextMods;
  }

  function toggleQuestion(quotaIds) {
    const next = new Set(selectedModalityIds.value);
    const allSelected = quotaIds.every((id) => next.has(id));
    if (allSelected) {
      quotaIds.forEach((id) => next.delete(id));
    } else {
      quotaIds.forEach((id) => next.add(id));
    }
    selectedModalityIds.value = next;
  }

  function toggleModality(quotaId) {
    const next = new Set(selectedModalityIds.value);
    if (next.has(quotaId)) next.delete(quotaId);
    else next.add(quotaId);
    selectedModalityIds.value = next;
  }

  function clearAll() {
    selectedTargetTokens.value = new Set();
    selectedModalityIds.value = new Set();
  }

  function propagateTargetField(targetsSource, currentToken, field, value) {
    const targets = toValue(targetsSource);
    if (!selectedTargetTokens.value.has(currentToken) || selectedTargetTokens.value.size <= 1) {
      const t = targets.find((x) => x.token === currentToken);
      if (t) t[field] = value;
      return;
    }
    targets.forEach((t) => {
      if (selectedTargetTokens.value.has(t.token)) t[field] = value;
    });
  }

  function propagateModalityField(quotasSource, currentId, field, value) {
    const quotas = toValue(quotasSource);
    if (!selectedModalityIds.value.has(currentId) || selectedModalityIds.value.size <= 1) {
      const q = quotas.find((x) => x.id === currentId);
      if (q) q[field] = value;
      return;
    }
    quotas.forEach((q) => {
      if (selectedModalityIds.value.has(q.id)) q[field] = value;
    });
  }

  return {
    selectedTargetTokens,
    selectedModalityIds,
    selectedCount,
    hasSelection,
    isTargetSelected,
    isModalitySelected,
    isQuestionSelected,
    toggleTarget,
    toggleQuestion,
    toggleModality,
    clearAll,
    propagateTargetField,
    propagateModalityField,
  };
}
